// @ts-nocheck
import React, { useCallback, useEffect, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import PropTypes from 'prop-types';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { scheduleDraftPersistOnBlur } from '../../../../hooks/useDraftAutoSave.jsx';
import { DRAFT_INPUT_SX } from './TextFieldComponent';

const getGroupSelectOptionLabel = (option) => option?.groupName;

/**
 * Group Select field component for selecting organization groups
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string|number} props.value - Current selected group name or id
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Function} [props.onNormalize] - Optional callback used when normalizing a group name
 *   to its numeric ID during initialization. If provided, this is called instead of onChange
 *   so the normalization does not trigger draft auto-save.
 * @param {Object} props.attribute - The attribute being edited
 * @param {Array} props.groupList - List of available groups
 * @returns {React.ReactElement} The GroupSelectFieldComponent
 */
const GroupSelectFieldComponent = ({
  value,
  label,
  titleLabel,
  onChange,
  onNormalize,
  groupList = [],
  attribute,
  isLoadingConstraints = false,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  /**
   * Handles group selection changes
   * @function
   * @param {Event} event - The change event
   * @param {Object} group - The selected group
   * @returns {void}
   */
  const isDerived = useMemo(
    () => attribute?.is_derived,
    [attribute?.is_derived]
  );

  const handleChange = useCallback(
    (_event, group) => {
      onChange(group ? group?.id : null);
      if (!group) {
        scheduleDraftPersistOnBlur(onDraftBlur);
      }
    },
    [onChange, onDraftBlur]
  );

  // Find the group object that matches the current value (whether it's a groupName or id)
  const selectedGroup = useMemo(() => {
    if (!value) return null;

    // Check if value is an id (number) or groupName (string)
    const isGroupId = !isNaN(Number(value));

    return groupList.find((group) => {
      if (isGroupId) {
        return group?.id === Number(value);
      } else {
        return group?.groupName === value;
      }
    });
  }, [groupList, value]);

  const renderGroupSelectTextField = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        placeholder={isDerived ? '-' : 'Select group'}
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params.slotProps.input,
            placeholder: 'Select group',
          },

          inputLabel: {
            ...params?.slotProps?.inputLabel,
            shrink: true,
          },
        }}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
      />
    ),
    [label, titleLabel, isDerived, hasDraftChip]
  );

  // Published values use groupName; drafts use group id — only convert name -> id.
  // Use onNormalize (if provided) instead of onChange so this initialization step
  // does not trigger draft auto-save (this is a display normalization, not a user action).
  useEffect(() => {
    if (value == null || value === '' || !isNaN(Number(value))) return;
    if (!groupList?.length) return;
    const matchedGroup = groupList.find((group) => group?.groupName === value);
    if (matchedGroup?.id != null) {
      const normalizeHandler = onNormalize ?? onChange;
      normalizeHandler(matchedGroup.id);
    }
  }, [value, groupList, onChange, onNormalize]);

  return (
    <span
      title={
        isDerived
          ? 'This field is disabled because it is derived'
          : `${attribute?.label}`
      }
    >
      <Autocomplete
        fullWidth
        disabled={isDerived}
        readOnly={isLoadingConstraints}
        value={selectedGroup || null}
        options={groupList}
        popupIcon={<DropdownArrow />}
        getOptionLabel={getGroupSelectOptionLabel}
        renderInput={renderGroupSelectTextField}
        onChange={handleChange}
        onClose={onDraftBlur}
        style={{ pointerEvents: isDerived ? 'none' : 'auto' }}
      />
    </span>
  );
};

GroupSelectFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onNormalize: PropTypes.func,
  attribute: PropTypes.shape({
    description: PropTypes.string,
    is_derived: PropTypes.bool.isRequired,
    label: PropTypes.string.isRequired,
    // Add other attribute properties as needed
  }).isRequired,
  groupList: PropTypes.arrayOf(
    PropTypes.shape({
      groupName: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      // Add other group properties as needed
    })
  ),
  isLoadingConstraints: PropTypes.bool,
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default GroupSelectFieldComponent;
